import { useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, FileText, Trash2, Upload, Mail, HardDrive } from 'lucide-react'
import { useTrip } from '../trips/useTrip'
import { usePoints } from '../timeline/usePoints'
import { useAddDocument, useDeleteDocument, useDocuments } from '../live/useToday'
import { FilterChips } from '../media/MediaLibrary'
import { GmailImportPanel } from './GmailImportPanel'
import DriveImportPanel from './DriveImportPanel'
import { Button } from '../../shared/components/Button'
import { Spinner } from '../../shared/components/Spinner'
import { EmptyState } from '../../shared/components/EmptyState'
import { OcrPanel } from './OcrPanel'
import type { Document } from '../../shared/api/types'
import { useTranslation } from 'react-i18next'
import { downloadDocumentFile } from '../../shared/lib/documentFiles'

type Filter = 'all' | 'ticket' | 'insurance' | 'general' | 'unbound'

export function DocumentsLibrary() {
  const { id } = useParams<{ id: string }>()
  const tripId = id as string
  const { t } = useTranslation()
  const trip = useTrip(id)
  const points = usePoints(id)
  const docs = useDocuments(id)
  const add = useAddDocument(tripId)
  const remove = useDeleteDocument(tripId)
  const fileRef = useRef<HTMLInputElement>(null)
  const [filter, setFilter] = useState<Filter>('all')
  const [gmailOpen, setGmailOpen] = useState(false)
  const [driveOpen, setDriveOpen] = useState(false)
  const [importedCount, setImportedCount] = useState(0)
  const [ocrDoc, setOcrDoc] = useState<Document | null>(null)
  const [fileError, setFileError] = useState('')

  const pointTitles = useMemo(() => {
    const m = new Map<string, string>()
    for (const p of points.data ?? []) m.set(p.id, p.title)
    return m
  }, [points.data])

  const filtered = useMemo(() => {
    const items = docs.data ?? []
    return items.filter((d) => {
      if (filter === 'unbound') return !d.point_id
      if (filter === 'all') return true
      return d.type === filter
    })
  }, [docs.data, filter])

  const onPick = () => fileRef.current?.click()
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) add.mutate({ file, type: 'general' })
    e.target.value = ''
  }

  if (docs.isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Spinner />
      </div>
    )
  }

  return (
    <>
    <div className="mx-auto max-w-3xl pb-12">
      <Link
        to={`/trips/${tripId}`}
        className="mb-4 inline-flex items-center gap-1 text-sm text-st-muted hover:text-st-text"
      >
        <ArrowLeft className="h-4 w-4" /> Back to overview
      </Link>

      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="flex h-10 w-10 items-center justify-center rounded-st-md bg-st-primary/10 text-st-primary">
            <FileText className="h-5 w-5" />
          </span>
          <div>
            <h1 className="font-display text-2xl font-bold text-st-text">Documents</h1>
            <p className="text-sm text-st-muted">{trip.data?.title}</p>
          </div>
        </div>
        <Button variant="outline" onClick={onPick} disabled={add.isPending}>
          <Upload className="h-4 w-4" /> {add.isPending ? 'Uploading…' : 'Upload'}
        </Button>
        <Button variant="ghost" onClick={() => setGmailOpen(true)}>
          <Mail className="h-4 w-4" /> Gmail Import
        </Button>
        <Button variant="ghost" onClick={() => setDriveOpen(true)}>
          <HardDrive className="h-4 w-4" /> Drive Import
        </Button>
        <input ref={fileRef} type="file" hidden onChange={onChange} />
      </div>

      <FilterChips
        options={[
          { id: 'all', label: 'All' },
          { id: 'ticket', label: 'Tickets' },
          { id: 'insurance', label: 'Insurance' },
          { id: 'general', label: 'General' },
          { id: 'unbound', label: 'Trip only' },
        ]}
        value={filter}
        onChange={setFilter}
      />
      {fileError && <p role="alert" className="mt-2 text-sm text-st-danger">{fileError}</p>}

      {filtered.length === 0 ? (
        <EmptyState
          Icon={FileText}
          title="No documents yet"
          description="Upload tickets, insurance, and passes — bind them to a place from Point Detail when you can."
        />
      ) : (
        <ul className="mt-4 space-y-2">
          {filtered.map((d) => (
            <li
              key={d.id}
              className="flex items-center gap-3 rounded-st-md border border-st-line bg-st-surface p-3"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-st-md bg-st-surface-2 text-st-primary">
                <FileText className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium capitalize text-st-text">{d.type}</p>
                <p className="truncate text-xs text-st-muted">
                  {d.point_id ? pointTitles.get(d.point_id) ?? 'Place' : 'Trip-wide'}
                  {' · '}
                  {d.file_url.split('/').pop()}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOcrDoc(d)}
                className="rounded-st-sm border border-st-line px-2 py-1 text-xs text-st-primary"
              >{t('ocr.read')}</button>
              <button type="button" className="rounded-st-sm border border-st-line px-2 py-1 text-xs text-st-primary"
                onClick={() => { void downloadDocumentFile(d).catch(() => setFileError(t('files.unavailable'))) }}>
                {t('files.open')}</button>
              <button
                type="button"
                onClick={() => remove.mutate(d.id)}
                className="rounded-st-sm p-1.5 text-st-muted hover:bg-st-danger/10 hover:text-st-danger"
                aria-label="Delete document"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>

    {gmailOpen && id && (
      <GmailImportPanel
        tripId={id}
        onClose={() => setGmailOpen(false)}
        onImported={(count) => { setImportedCount(count); setGmailOpen(false) }}
      />
    )}

    {driveOpen && id && (
      <DriveImportPanel
        tripId={id}
        onClose={() => setDriveOpen(false)}
        onImported={(docs, media) => {
          const total = docs + media
          setImportedCount(total)
          setDriveOpen(false)
        }}
      />
    )}

    {importedCount > 0 && (
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 rounded-st-lg border border-green-200 bg-green-50 px-4 py-2 shadow-lg">
        <p className="text-sm font-medium text-green-800">
          {importedCount} item{importedCount > 1 ? 's' : ''} added to your trip
        </p>
      </div>
    )}
    {ocrDoc && <OcrPanel tripId={tripId} doc={ocrDoc} onClose={() => setOcrDoc(null)} />}
    </>
  )
}
